// A league game played in a room, /room/lg-<league>-<game> (the league and game come from the room's
// name, so a pasted link works too);
// the host's browser sets the room up from the league (Conquer the Flag, both armies, a bot for a bot
// player), starts it with the league's Blue and Red, and any player's browser reports the result.
import { useEffect, useState } from 'react';
import { getLeague, leagueToken } from '../../leagues/api';
import type { Army } from '../../leagues/api';
import { improvedCode } from '../../leagues/improvements';
import type { ParticipantView, RoomView } from '../../types/room';

export interface LeagueSide { id: string; name: string; bot: boolean; code: string; army: string; pending: number; armyData?: Army }
export interface LeagueRoomInfo {
  leagueId: string;
  leagueName: string;
  gameId: string;
  round: number;      // 1-based
  blue: LeagueSide;
  red: LeagueSide;
  options: Record<string, string | number | boolean>;   // the league's game settings (e.g. prisons)
  settled?: string | null;   // the league already has this game's result (or voided it): what it says
}

// "lg-autumn-flag-1a2b3c-r2g1" → the league and the game.
export function leagueGameOfSlug(slug: string): { leagueId: string; gameId: string } | null {
  const m = slug.match(/^lg-(.+)-(r\d+g\d+)$/);
  return m ? { leagueId: m[1], gameId: m[2] } : null;
}

// The game, from the league server (null until loaded, or if it isn't a playable league game), and who
// you are in the league if this browser holds a seat (`myName`; '' while loading, null if you hold none).
export function useLeagueGame(leagueId: string | null, gameId: string | null): { info: LeagueRoomInfo | null; problem: string | null; myName: string | null } {
  const [info, setInfo] = useState<LeagueRoomInfo | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [myName, setMyName] = useState<string | null>(() => (leagueId && leagueToken(leagueId) ? '' : null));
  const [tick, setTick] = useState(0);
  // Check again every few seconds: an improvement chosen while the room is open changes that army.
  // Until the game has loaded (e.g. the connection dropped), try again sooner.
  const loaded = !!info;
  useEffect(() => {
    if (!leagueId) return;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') setTick(n => n + 1); }, loaded ? 8000 : 3000);
    return () => window.clearInterval(t);
  }, [leagueId, loaded]);
  useEffect(() => {
    if (!leagueId || !gameId) return;
    getLeague(leagueId).then(L => {
      setMyName(L.me ? L.players.find(p => p.id === L.me!.id)?.name ?? null : null);
      const ri = L.rounds.findIndex(r => r.games.some(g => g.id === gameId));
      const g = ri >= 0 ? L.rounds[ri].games.find(x => x.id === gameId)! : null;
      if (!g) { setProblem('This league game was not found.'); return; }
      const side = (id: string): LeagueSide | null => {
        const p = L.players.find(q => q.id === id);
        return p && p.army?.code ? { id, name: p.name, bot: p.bot, code: improvedCode(p.army) ?? p.army.code, army: p.army.name, pending: p.pending ?? 0, armyData: { name: p.army.name, units: p.army.units, improvements: p.army.improvements, code: p.army.code } } : null;
      };
      const blue = side(g.blue), red = side(g.red);
      if (!blue || !red) { setProblem("The league's armies aren't ready."); return; }
      const name = (pid: string | null) => L.players.find(q => q.id === pid)?.name ?? '?';
      const settled = g.voided ? 'the league voided it' : g.result ? (g.result.winner ? `${name(g.result.winner)} won${g.result.how ? ` (${g.result.how})` : ''}` : 'a draw') : null;
      const next = { leagueId: L.id, leagueName: L.name, gameId, round: ri + 1, blue, red, options: L.settings.gameOptions ?? {}, settled };
      setInfo(prev => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      setProblem(null);
    }).catch(e => {
      // No answer at all (a dropped connection): keep trying, and keep waiting to know who you are.
      if (e instanceof TypeError) { setProblem("Couldn't reach the league. Trying again…"); return; }
      setProblem((e as Error).message);
      setMyName(m => (m === '' ? null : m));
    });
  }, [leagueId, gameId, tick]);
  return { info, problem, myName };
}

export const leagueOfRoom = (room: RoomView): LeagueRoomInfo | null =>
  (room.config as { league?: LeagueRoomInfo }).league ?? null;

// The room's people playing the league's Blue and Red (matched by name; bots by bot name).
export function leagueSeats(room: RoomView, info: LeagueRoomInfo): { blue?: ParticipantView; red?: ParticipantView } {
  const find = (s: LeagueSide) => room.participants.find(p => (s.bot ? p.type === 'bot' : p.type !== 'bot') && p.name.trim().toLowerCase() === s.name.trim().toLowerCase());
  return { blue: find(info.blue), red: find(info.red) };
}

// "Blue" / "Red" by the league's sides (not by seat order), for seat lists and the in-game bar.
export function leagueSideName(info: LeagueRoomInfo, p: ParticipantView): string {
  const is = (s: LeagueSide) => (s.bot ? p.type === 'bot' : p.type !== 'bot') && p.name.trim().toLowerCase() === s.name.trim().toLowerCase();
  return is(info.blue) ? 'Blue' : is(info.red) ? 'Red' : 'Watching';
}

// A nicknamed unit's type ("Big Wally" → "Warden"), for the game's unit panel. Blue is team A.
export function leagueUnitType(info: LeagueRoomInfo, team: 'A' | 'B', name: string): string | undefined {
  const u = (team === 'A' ? info.blue : info.red).armyData?.units?.find(x => x.nick && x.nick === name);
  return u?.type;
}
